/**
 * INVI HQ — Scheduled Reports. Operator/business-side scheduling: pick a report,
 * recipient emails, and frequency; the backend emails it on cadence. System-admin
 * only (route-gated by view_insights). A separate dealer-facing scheduled-reports
 * feature will live on its own later.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import Layout from "../../components/Layout"
import { THEME } from "../../config/theme"
import { useAuth } from "../../context/AuthContext"
import {
  ReportTypeOption,
  ScheduledReport,
  createScheduledReport,
  deleteScheduledReport,
  fetchReportTypes,
  fetchScheduledReports,
  runScheduledReportNow,
  updateScheduledReport,
} from "../../api/scheduledReports"

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

// "interval" fires every N minutes instead of at a wall-clock time — the cadence
// an alert needs, since breakage doesn't wait for 8am. It ignores hour/minute.
const FREQUENCIES = ["daily", "weekly", "monthly", "interval"] as const
const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  interval: "Every few minutes (alerts)",
}
const DEFAULT_INTERVAL_MINUTES = 15
const MIN_INTERVAL_MINUTES = 5

interface FormState {
  report_type: string
  recipientsText: string
  frequency: string
  day_of_week: number
  day_of_month: number
  interval_minutes: number
  time: string // "HH:MM"
  is_enabled: boolean
}

const EMPTY_FORM: FormState = {
  report_type: "",
  recipientsText: "",
  frequency: "weekly",
  day_of_week: 0,
  day_of_month: 1,
  interval_minutes: DEFAULT_INTERVAL_MINUTES,
  time: "08:00",
  is_enabled: true,
}

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? "PM" : "AM"
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12}:${String(minute).padStart(2, "0")} ${period} PT`
}

function scheduleLabel(r: ScheduledReport): string {
  if (r.frequency === "interval") return `Every ${r.interval_minutes} min`
  const time = formatTime(r.hour, r.minute)
  if (r.frequency === "daily") return `Daily · ${time}`
  if (r.frequency === "weekly") return `Weekly · ${DOW_LABELS[r.day_of_week ?? 0]} · ${time}`
  if (r.frequency === "monthly") return `Monthly · day ${r.day_of_month} · ${time}`
  return r.frequency
}

function ScheduledReportsPage() {
  const { hasPermission, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [types, setTypes] = useState<ReportTypeOption[]>([])
  const [reports, setReports] = useState<ScheduledReport[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [modalError, setModalError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [rowBusyId, setRowBusyId] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!hasPermission("view_insights")) navigate("/dashboard", { replace: true })
  }, [authLoading, hasPermission, navigate])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [typeList, reportList] = await Promise.all([
        fetchReportTypes(),
        fetchScheduledReports(),
      ])
      setTypes(typeList)
      setReports(reportList)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load scheduled reports")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authLoading || !hasPermission("view_insights")) return
    load()
  }, [authLoading, hasPermission, load])

  const typeLabel = useMemo(() => {
    const map: Record<string, string> = {}
    types.forEach((t) => (map[t.key] = t.label))
    return map
  }, [types])

  const selectedType = useMemo(
    () => types.find((t) => t.key === form.report_type),
    [types, form.report_type],
  )

  const openNew = () => {
    setEditingId(null)
    const first = types[0]
    setForm({
      ...EMPTY_FORM,
      report_type: first?.key ?? "",
      frequency: first?.default_frequency ?? EMPTY_FORM.frequency,
    })
    setModalError(null)
    setModalOpen(true)
  }

  // Each report knows the cadence it's for. Switching the report moves the
  // frequency with it, so an Error Alert never sits on a weekly send — which
  // would report Monday's broken upload the following Monday.
  const selectReportType = (key: string) => {
    const picked = types.find((t) => t.key === key)
    setForm((prev) => ({
      ...prev,
      report_type: key,
      frequency: picked?.default_frequency ?? prev.frequency,
    }))
  }

  const openEdit = (r: ScheduledReport) => {
    setEditingId(r.id)
    setForm({
      report_type: r.report_type,
      recipientsText: r.recipients.join(", "),
      frequency: r.frequency,
      day_of_week: r.day_of_week ?? 0,
      day_of_month: r.day_of_month ?? 1,
      interval_minutes: r.interval_minutes ?? DEFAULT_INTERVAL_MINUTES,
      time: `${String(r.hour).padStart(2, "0")}:${String(r.minute).padStart(2, "0")}`,
      is_enabled: r.is_enabled,
    })
    setModalError(null)
    setModalOpen(true)
  }

  const save = async () => {
    setModalError(null)
    const recipients = form.recipientsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (!form.report_type) {
      setModalError("Pick a report")
      return
    }
    if (recipients.length === 0) {
      setModalError("Add at least one recipient email")
      return
    }
    if (form.frequency === "interval" && form.interval_minutes < MIN_INTERVAL_MINUTES) {
      setModalError(`Interval must be at least ${MIN_INTERVAL_MINUTES} minutes`)
      return
    }
    const [hourStr, minStr] = form.time.split(":")
    const payload = {
      report_type: form.report_type,
      recipients,
      frequency: form.frequency,
      day_of_week: form.frequency === "weekly" ? form.day_of_week : null,
      day_of_month: form.frequency === "monthly" ? form.day_of_month : null,
      interval_minutes: form.frequency === "interval" ? form.interval_minutes : null,
      hour: Number(hourStr),
      minute: Number(minStr),
      is_enabled: form.is_enabled,
    }
    setSaving(true)
    try {
      if (editingId === null) {
        await createScheduledReport(payload)
      } else {
        await updateScheduledReport(editingId, payload)
      }
      setModalOpen(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const runRow = async (r: ScheduledReport, fn: () => Promise<void>, successNote?: string) => {
    setRowBusyId(r.id)
    setNotice(null)
    setError(null)
    try {
      await fn()
      if (successNote) setNotice(successNote)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed")
    } finally {
      setRowBusyId(null)
    }
  }

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------

  const inputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: "40px",
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    padding: "9px 12px",
    fontSize: "14px",
    fontFamily: THEME.fonts.primary,
    color: THEME.colors.neutral.light.textPrimary,
    boxSizing: "border-box",
    marginBottom: "12px",
    outline: "none",
  }
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "4px",
  }
  const primaryBtn: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    background: THEME.colors.navy,
    color: THEME.colors.white,
    fontWeight: 500,
    fontSize: "14px",
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
  }
  const secondaryBtn: React.CSSProperties = {
    ...primaryBtn,
    background: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    border: `1px solid ${THEME.colors.silver}`,
  }
  const linkBtn: React.CSSProperties = {
    background: "none",
    border: "none",
    color: THEME.colors.navy,
    fontWeight: 500,
    fontSize: "13px",
    cursor: "pointer",
    padding: "0 6px",
    fontFamily: THEME.fonts.primary,
  }

  if (authLoading || !hasPermission("view_insights")) {
    return (
      <Layout>
        <div />
      </Layout>
    )
  }

  return (
    <Layout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div
          style={{
            fontSize: THEME.typography.h3.size,
            fontWeight: THEME.typography.h3.weight,
            letterSpacing: THEME.typography.h3.tracking,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          Scheduled Reports
        </div>
        <button type="button" style={primaryBtn} onClick={openNew} disabled={types.length === 0}>
          + New schedule
        </button>
      </div>
      <div style={{ fontSize: "13px", color: THEME.colors.neutral.light.textSecondary, marginBottom: "20px" }}>
        Email INVI business reports to anyone on a daily, weekly, or monthly cadence — or set up an
        Error Alert that emails failures as they happen. Times are Pacific.
      </div>

      {notice && (
        <div style={{ fontSize: "13px", color: THEME.colors.semantic.success, marginBottom: "12px" }}>{notice}</div>
      )}
      {error && (
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            color: THEME.colors.semantic.danger,
            border: "0.5px solid rgba(220, 38, 38, 0.3)",
            padding: "10px 14px",
            borderRadius: THEME.borderRadius.md,
            marginBottom: "16px",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: THEME.colors.neutral.light.surface,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.colors.silver}`,
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", fontFamily: THEME.fonts.primary }}>
          <thead>
            <tr style={{ background: THEME.auditTable.titleBarBackgroundLight }}>
              {["Report", "Recipients", "Schedule", "Status", "Last sent", "Actions"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    fontWeight: 500,
                    fontSize: "12px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: THEME.colors.neutral.light.textSecondary,
                    borderBottom: `1px solid ${THEME.colors.silver}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={6} style={emptyCell}>No scheduled reports yet. Create one to start.</td></tr>
            ) : (
              reports.map((r, idx) => (
                <tr
                  key={r.id}
                  style={{ background: idx % 2 === 0 ? THEME.colors.neutral.light.surface : THEME.colors.neutral.light.background }}
                >
                  <td style={cell}>{typeLabel[r.report_type] ?? r.report_type}</td>
                  <td style={cell}>{r.recipients.join(", ")}</td>
                  <td style={cell}>{scheduleLabel(r)}</td>
                  <td style={cell}>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: r.is_enabled ? THEME.colors.semantic.success : THEME.colors.neutral.light.textSecondary,
                      }}
                    >
                      {r.is_enabled ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td style={cell}>
                    {r.last_sent_at
                      ? new Date(r.last_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                      : "—"}
                  </td>
                  <td style={cell}>
                    <button type="button" style={linkBtn} onClick={() => openEdit(r)}>Edit</button>
                    <span style={{ color: THEME.colors.silver }}>|</span>
                    <button
                      type="button"
                      style={linkBtn}
                      disabled={rowBusyId === r.id}
                      onClick={() =>
                        runRow(r, async () => {
                          // An alert with no new failures is a successful run that
                          // mails nobody — say that rather than claiming it sent.
                          const sent = await runScheduledReportNow(r.id)
                          setNotice(sent ? "Sent now." : "Nothing to send — no new errors since the last alert.")
                        })
                      }
                    >
                      Run now
                    </button>
                    <span style={{ color: THEME.colors.silver }}>|</span>
                    <button
                      type="button"
                      style={linkBtn}
                      disabled={rowBusyId === r.id}
                      onClick={() => runRow(r, () => updateScheduledReport(r.id, { is_enabled: !r.is_enabled }).then(() => {}))}
                    >
                      {r.is_enabled ? "Pause" : "Resume"}
                    </button>
                    <span style={{ color: THEME.colors.silver }}>|</span>
                    <button
                      type="button"
                      style={{ ...linkBtn, color: THEME.colors.semantic.danger }}
                      disabled={rowBusyId === r.id}
                      onClick={() => runRow(r, () => deleteScheduledReport(r.id))}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "460px", maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto",
              background: THEME.colors.neutral.light.surface,
              border: `1px solid ${THEME.colors.silver}`,
              borderRadius: THEME.borderRadius.lg, padding: "28px",
              boxSizing: "border-box", fontFamily: THEME.fonts.primary,
            }}
          >
            <div style={{ fontSize: "20px", fontWeight: 500, color: THEME.colors.neutral.light.textPrimary, marginBottom: "20px" }}>
              {editingId === null ? "New schedule" : "Edit schedule"}
            </div>

            <label style={labelStyle}>Report</label>
            <select
              value={form.report_type}
              onChange={(e) => selectReportType(e.target.value)}
              style={{ ...inputStyle, marginBottom: "4px" }}
            >
              {types.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            {selectedType?.description && (
              <div style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary, marginBottom: "12px" }}>
                {selectedType.description}
              </div>
            )}

            <label style={labelStyle}>Recipients</label>
            <textarea
              value={form.recipientsText}
              onChange={(e) => setForm({ ...form, recipientsText: e.target.value })}
              placeholder="email@example.com, another@example.com"
              style={{ ...inputStyle, minHeight: "64px", resize: "vertical" }}
            />

            <label style={labelStyle}>Frequency</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              style={inputStyle}
            >
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
              ))}
            </select>

            {form.frequency === "interval" && (
              <>
                <label style={labelStyle}>Check every (minutes)</label>
                <input
                  type="number"
                  min={MIN_INTERVAL_MINUTES}
                  max={1440}
                  value={form.interval_minutes}
                  onChange={(e) => setForm({ ...form, interval_minutes: Number(e.target.value) })}
                  style={{ ...inputStyle, marginBottom: "4px" }}
                />
                <div style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary, marginBottom: "12px" }}>
                  Nothing is emailed when nothing broke.
                </div>
              </>
            )}

            {form.frequency === "weekly" && (
              <>
                <label style={labelStyle}>Day of week</label>
                <select
                  value={form.day_of_week}
                  onChange={(e) => setForm({ ...form, day_of_week: Number(e.target.value) })}
                  style={inputStyle}
                >
                  {DOW_LABELS.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </>
            )}
            {form.frequency === "monthly" && (
              <>
                <label style={labelStyle}>Day of month (1–28)</label>
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })}
                  style={inputStyle}
                />
              </>
            )}

            {form.frequency !== "interval" && (
              <>
                <label style={labelStyle}>Time (Pacific)</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  style={inputStyle}
                />
              </>
            )}

            <label style={{ display: "flex", alignItems: "center", fontSize: "14px", color: THEME.colors.neutral.light.textPrimary, marginBottom: "16px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                style={{ marginRight: "8px" }}
              />
              Active
            </label>

            {modalError && (
              <div style={{ color: THEME.colors.semantic.danger, fontSize: "13px", marginBottom: "12px" }}>{modalError}</div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button type="button" style={secondaryBtn} onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}>
                {saving ? "Saving…" : editingId === null ? "Create schedule" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

const cell: React.CSSProperties = {
  padding: "10px 14px",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  color: THEME.colors.neutral.light.textPrimary,
  verticalAlign: "middle",
}

const emptyCell: React.CSSProperties = {
  padding: "24px",
  textAlign: "center",
  color: THEME.colors.neutral.light.textSecondary,
}

export default ScheduledReportsPage
